"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useEffect, useState } from "react";
import { fetchTikTokPreview, getCachedTikTokPreview, type TikTokPreviewResult, type TikTokPreviewSuccess, type TikTokPreviewUnavailable } from "@/services/api/tiktok-preview";
import { LinkPreviewCardLayout } from "./OpenGraphPreview";
interface TikTokCardProps {
  readonly href: string;
}
type TikTokCardState = {
  status: "loading";
} | {
  status: "success";
  data: TikTokPreviewSuccess;
} | {
  status: "unavailable";
  canonicalUrl?: string | undefined;
};
const CAPTION_PREVIEW_LIMIT = 180;
function isUnavailable(preview: TikTokPreviewResult): preview is TikTokPreviewUnavailable {
  return (preview as {
    error?: string | undefined;
  }).error === "unavailable";
}
function extractUsername(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }
  try {
    const parsed = new URL(url);
    const first = parsed.pathname.split("/").find(segment => segment.length > 0);
    if (first?.startsWith("@")) {
      return first;
    }
  } catch {
    // ignore parsing errors
  }
  return null;
}
function buildAltText(data: TikTokPreviewSuccess, username: string | null): string {
  if (data.kind === "profile") {
    if (username) {
      return `TikTok profile ${username}`;
    }
    if (data.authorName) {
      return `TikTok profile ${data.authorName}`;
    }
    return "TikTok profile";
  }
  if (data.authorName) {
    return `TikTok video by ${data.authorName}`;
  }
  if (username) {
    return `TikTok video by ${username}`;
  }
  return "TikTok video";
}
function buildAuthorLink(data: TikTokPreviewSuccess, username: string | null): string | undefined {
  if (data.authorUrl) {
    return data.authorUrl;
  }
  if (username) {
    return `https://www.tiktok.com/${username}`;
  }
  return undefined;
}
export default function TikTokCard(t0) {
  const $ = _c(70);
  const {
    href
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      status: "loading"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [state, setState] = useState(t1);
  const [isCaptionExpanded, setIsCaptionExpanded] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  let t2;
  let t3;
  if ($[1] !== href) {
    t2 = () => {
      let active = true;
      setIsCaptionExpanded(false);
      setThumbnailError(false);
      const cached = getCachedTikTokPreview(href);
      if (cached) {
        if (isUnavailable(cached.data)) {
          setState({
            status: "unavailable",
            canonicalUrl: cached.data.canonicalUrl ?? href
          });
        } else {
          setState({
            status: "success",
            data: cached.data
          });
        }
        if (cached.isFresh) {
          return () => {
            active = false;
          };
        }
      } else {
        setState({
          status: "loading"
        });
      }
      fetchTikTokPreview(href).then(result => {
        if (!active) {
          return;
        }
        if (isUnavailable(result)) {
          setState({
            status: "unavailable",
            canonicalUrl: result.canonicalUrl ?? href
          });
          return;
        }
        setState({
          status: "success",
          data: result
        });
      }).catch(() => {
        if (active) {
          setState({
            status: "unavailable",
            canonicalUrl: href
          });
        }
      });
      return () => {
        active = false;
      };
    };
    t3 = [href];
    $[1] = href;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  if (state.status === "loading") {
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="tw-w-full md:tw-w-44 md:tw-flex-shrink-0"><div className="tw-aspect-[9/16] tw-w-full tw-rounded-lg tw-bg-iron-800/60 tw-animate-pulse" /></div>;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    let t6;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="tw-h-3 tw-w-16 tw-rounded tw-bg-iron-800/50 tw-animate-pulse" />;
      t6 = <div className="tw-h-5 tw-w-40 tw-rounded tw-bg-iron-800/40 tw-animate-pulse" />;
      $[5] = t5;
      $[6] = t6;
    } else {
      t5 = $[5];
      t6 = $[6];
    }
    let t7;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4" data-testid="tiktok-card-skeleton"><div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row">{t4}<div className="tw-flex tw-flex-1 tw-flex-col tw-gap-y-3">{t5}{t6}<div className="tw-space-y-2"><div className="tw-h-4 tw-w-full tw-rounded tw-bg-iron-800/30 tw-animate-pulse" /><div className="tw-h-4 tw-w-3/4 tw-rounded tw-bg-iron-800/20 tw-animate-pulse" /><div className="tw-h-4 tw-w-2/3 tw-rounded tw-bg-iron-800/10 tw-animate-pulse" /></div></div></div></div>;
      $[7] = t7;
    } else {
      t7 = $[7];
    }
    let t8;
    if ($[8] !== href) {
      t8 = <LinkPreviewCardLayout href={href}>{t7}</LinkPreviewCardLayout>;
      $[8] = href;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    return t8;
  }
  const canonicalUrl = state.status === "success" ? state.data.canonicalUrl : state.canonicalUrl ?? href;
  if (state.status === "unavailable") {
    let t4;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-iron-200">This TikTok is unavailable or private.</p>;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== canonicalUrl) {
      t5 = <div className="tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4" data-testid="tiktok-card-unavailable"><div className="tw-flex tw-flex-col tw-items-center tw-gap-y-3 tw-text-center">{t4}<Link href={canonicalUrl} target="_blank" rel="noopener noreferrer" className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-primary-500 tw-bg-primary-500 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-no-underline tw-transition tw-duration-200 hover:tw-bg-primary-400 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400" aria-label="Open this TikTok on TikTok">Open on TikTok</Link></div></div>;
      $[11] = canonicalUrl;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== canonicalUrl || $[14] !== t5) {
      t6 = <LinkPreviewCardLayout href={canonicalUrl}>{t5}</LinkPreviewCardLayout>;
      $[13] = canonicalUrl;
      $[14] = t5;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    return t6;
  }
  const data = state.data;
  let aspectRatio;
  let authorLink;
  let caption;
  let isLongCaption;
  let primaryAuthor;
  let provider;
  let secondaryAuthor;
  let t4;
  let visibleCaption;
  if ($[16] !== data || $[17] !== isCaptionExpanded) {
    const username = extractUsername(data.authorUrl) ?? extractUsername(data.canonicalUrl) ?? null;
    provider = data.providerName ?? "TikTok";
    authorLink = buildAuthorLink(data, username);
    primaryAuthor = username ?? data.authorName ?? null;
    secondaryAuthor = username && data.authorName && username.toLowerCase() !== data.authorName.toLowerCase() ? data.authorName : null;
    caption = data.title ?? "";
    isLongCaption = caption.length > CAPTION_PREVIEW_LIMIT;
    let t5;
    if ($[27] !== caption || $[28] !== isCaptionExpanded || $[29] !== isLongCaption) {
      t5 = isCaptionExpanded || !isLongCaption ? caption : `${caption.slice(0, CAPTION_PREVIEW_LIMIT).trimEnd()}…`;
      $[27] = caption;
      $[28] = isCaptionExpanded;
      $[29] = isLongCaption;
      $[30] = t5;
    } else {
      t5 = $[30];
    }
    visibleCaption = t5;
    aspectRatio = data.thumbnailWidth && data.thumbnailHeight ? `${data.thumbnailWidth} / ${data.thumbnailHeight}` : "9 / 16";
    t4 = buildAltText(data, username);
    $[16] = data;
    $[17] = isCaptionExpanded;
    $[18] = aspectRatio;
    $[19] = authorLink;
    $[20] = caption;
    $[21] = isLongCaption;
    $[22] = primaryAuthor;
    $[23] = provider;
    $[24] = secondaryAuthor;
    $[25] = t4;
    $[26] = visibleCaption;
  } else {
    aspectRatio = $[18];
    authorLink = $[19];
    caption = $[20];
    isLongCaption = $[21];
    primaryAuthor = $[22];
    provider = $[23];
    secondaryAuthor = $[24];
    t4 = $[25];
    visibleCaption = $[26];
  }
  const thumbnailAlt = t4;
  const openHref = canonicalUrl ?? href;
  let t5;
  if ($[31] !== aspectRatio) {
    t5 = {
      aspectRatio
    };
    $[31] = aspectRatio;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  let t6;
  if ($[33] !== data.thumbnailUrl || $[34] !== thumbnailAlt || $[35] !== thumbnailError) {
    t6 = data.thumbnailUrl && !thumbnailError ? <img src={data.thumbnailUrl} alt={thumbnailAlt} loading="lazy" decoding="async" className="tw-h-full tw-w-full tw-object-cover" onError={() => setThumbnailError(true)} /> : <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-bg-iron-900/70"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="tw-h-10 tw-w-10 tw-text-iron-400" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 5.25a2.25 2.25 0 0 1 2.25-2.25h6l6 6v9.75a2.25 2.25 0 0 1-2.25 2.25h-9a2.25 2.25 0 0 1-2.25-2.25z" /><path strokeLinecap="round" strokeLinejoin="round" d="M14.25 3v4.5h4.5" /></svg></div>;
    $[33] = data.thumbnailUrl;
    $[34] = thumbnailAlt;
    $[35] = thumbnailError;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  let t7;
  if ($[37] !== data.kind) {
    t7 = data.kind === "video" && <div className="tw-absolute tw-bottom-2 tw-left-2 tw-flex tw-items-center tw-gap-x-2 tw-rounded-full tw-bg-black/70 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-white"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="tw-h-3.5 tw-w-3.5" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg><span>Video</span></div>;
    $[37] = data.kind;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  let t8;
  if ($[39] !== t5 || $[40] !== t6 || $[41] !== t7) {
    t8 = <div className="tw-w-full md:tw-w-44 md:tw-flex-shrink-0"><div className="tw-relative tw-overflow-hidden tw-rounded-lg tw-bg-iron-900/60" style={t5}>{t6}{t7}</div></div>;
    $[39] = t5;
    $[40] = t6;
    $[41] = t7;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  let t9;
  if ($[43] !== provider) {
    t9 = <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-iron-400">{provider}</span>;
    $[43] = provider;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  let t10;
  if ($[45] !== authorLink || $[46] !== openHref || $[47] !== primaryAuthor || $[48] !== secondaryAuthor) {
    t10 = primaryAuthor && <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-2 tw-gap-y-1"><Link href={authorLink ?? openHref} target="_blank" rel="noopener noreferrer" className="tw-text-base tw-font-semibold tw-text-iron-100 tw-no-underline tw-transition tw-duration-200 hover:tw-text-white">{primaryAuthor}</Link>{secondaryAuthor && <span className="tw-text-sm tw-text-iron-300">{secondaryAuthor}</span>}</div>;
    $[45] = authorLink;
    $[46] = openHref;
    $[47] = primaryAuthor;
    $[48] = secondaryAuthor;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  let t11;
  if ($[50] !== t10 || $[51] !== t9) {
    t11 = <div className="tw-flex tw-flex-col tw-gap-y-1">{t9}{t10}</div>;
    $[50] = t10;
    $[51] = t9;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  let t12;
  if ($[53] !== caption || $[54] !== isCaptionExpanded || $[55] !== isLongCaption || $[56] !== visibleCaption) {
    t12 = caption && <div className="tw-space-y-2"><p className="tw-m-0 tw-text-sm tw-text-iron-200 tw-whitespace-pre-wrap">{visibleCaption}</p>{isLongCaption && <button type="button" className="tw-text-xs tw-font-semibold tw-text-primary-400 tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer hover:tw-text-primary-300 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400" onClick={() => setIsCaptionExpanded(_temp)} aria-expanded={isCaptionExpanded}>{isCaptionExpanded ? "Show less" : "Show more"}</button>}</div>;
    $[53] = caption;
    $[54] = isCaptionExpanded;
    $[55] = isLongCaption;
    $[56] = visibleCaption;
    $[57] = t12;
  } else {
    t12 = $[57];
  }
  let t13;
  if ($[58] !== openHref) {
    t13 = <div><Link href={openHref} target="_blank" rel="noopener noreferrer" className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-primary-500 tw-bg-primary-500 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-no-underline tw-transition tw-duration-200 hover:tw-bg-primary-400 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400" aria-label="Open this TikTok on TikTok">Open on TikTok</Link></div>;
    $[58] = openHref;
    $[59] = t13;
  } else {
    t13 = $[59];
  }
  let t14;
  if ($[60] !== t11 || $[61] !== t12 || $[62] !== t13) {
    t14 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-y-3">{t11}{t12}{t13}</div>;
    $[60] = t11;
    $[61] = t12;
    $[62] = t13;
    $[63] = t14;
  } else {
    t14 = $[63];
  }
  let t15;
  if ($[64] !== t14 || $[65] !== t8) {
    t15 = <article className="tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4" data-testid="tiktok-card"><div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row">{t8}{t14}</div></article>;
    $[64] = t14;
    $[65] = t8;
    $[66] = t15;
  } else {
    t15 = $[66];
  }
  let t16;
  if ($[67] !== openHref || $[68] !== t15) {
    t16 = <LinkPreviewCardLayout href={openHref}>{t15}</LinkPreviewCardLayout>;
    $[67] = openHref;
    $[68] = t15;
    $[69] = t16;
  } else {
    t16 = $[69];
  }
  return t16;
}
function _temp(prev) {
  return !prev;
}